import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
} from 'recharts';
import {
  Store, CreditCard, Banknote, FileText, LayoutDashboard,
  Link2, Copy, Download, CheckCircle, Clock, XCircle,
  ChevronLeft, ChevronRight, Plus, QrCode, Power,
  Key, Wallet, Webhook, Eye, EyeOff, Trash2, Receipt, Settings, Send,
} from 'lucide-react';
import {
  Button, Card, Badge, StatCard, Modal, Input, Select,
  Spinner, Empty, Table, Td, SectionHeader, ProgressBar,
} from '../components/ui';
import { merchantAPI, merchantChargesAPI, invoiceAPI, settlementAPI } from '../services/api';

/* ─── helpers ────────────────────────────────────────────────────── */
const fmt = (n, cur = 'UGX') =>
  new Intl.NumberFormat('en-UG', { style: 'currency', currency: cur, minimumFractionDigits: 0 }).format(n ?? 0);

const truncate = (str, len = 16) =>
  str ? (str.length > len ? `${str.slice(0, len)}…` : str) : '—';

const statusColor = (s) =>
  ({ completed: 'green', pending: 'amber', failed: 'red', paid: 'green', processing: 'amber', cancelled: 'red' }[s?.toLowerCase()] || 'gray');

const TABS = [
  { id: 'dashboard',     label: 'Dashboard',      icon: LayoutDashboard },
  { id: 'charges',       label: 'Charges',        icon: CreditCard },
  { id: 'links',         label: 'Payment Links',  icon: Link2 },
  { id: 'payments',      label: 'Payments',       icon: Banknote },
  { id: 'api-keys',      label: 'API Keys',       icon: Key },
  { id: 'webhooks',      label: 'Webhooks',       icon: Webhook },
  { id: 'balance',       label: 'Balance',        icon: Wallet },
  { id: 'settlements',   label: 'Settlements',    icon: Banknote },
  { id: 'invoices',      label: 'Invoices',       icon: Receipt },
  { id: 'reports',       label: 'Reports',        icon: FileText },
];

/* ─── Custom Tooltip ─────────────────────────────────────────────── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-bg-surface border border-border rounded-xl px-3 py-2 text-xs">
      <p className="text-text-muted mb-1">{label}</p>
      <p className="text-cyan font-bold">{fmt(payload[0]?.value)}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════ */
export default function Merchant() {
  const [tab, setTab]             = useState('dashboard');
  const [profileReady, setProfileReady] = useState(null); // null=loading, false=none, true=ok

  useEffect(() => {
    merchantAPI.getProfile()
      .then(() => setProfileReady(true))
      .catch(() => setProfileReady(false));
  }, []);

  if (profileReady === null) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (profileReady === false) {
    return (
      <div className="min-h-screen bg-bg p-4 md:p-6 flex items-center justify-center">
        <Card className="max-w-md w-full text-center space-y-4 p-8">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
            <Store className="w-7 h-7 text-amber-400" />
          </div>
          <h2 className="text-text-primary font-bold text-lg">No Merchant Account</h2>
          <p className="text-text-muted text-sm leading-relaxed">
            Your account is not registered as a merchant on this platform.
            To accept payments and use the Merchant API, a merchant profile must be created for your account.
          </p>
          <div className="bg-bg-surface border border-border rounded-xl p-4 text-left space-y-1">
            <p className="text-xs font-semibold text-text-secondary">How to get a merchant account</p>
            <p className="text-xs text-text-muted">If you are the platform admin, go to <span className="text-cyan font-medium">Admin → Merchants → Register Merchant</span> and link it to your user account.</p>
            <p className="text-xs text-text-muted">Otherwise, contact your platform administrator to register your business.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg p-4 md:p-6 space-y-6">
      {/* Page heading */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-cyan/10 border border-cyan/20">
          <Store className="w-5 h-5 text-cyan" />
        </div>
        <div>
          <h1 className="text-text-primary font-black text-xl tracking-tight">Merchant Portal</h1>
          <p className="text-text-muted text-xs">Manage your business payments and settlements</p>
        </div>
      </div>

      {/* Tab pills */}
      <div className="flex flex-wrap gap-2">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              tab === id
                ? 'bg-cyan text-bg'
                : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'dashboard'   && <DashboardTab />}
      {tab === 'charges'     && <ChargesTab />}
      {tab === 'links'       && <LinksTab />}
      {tab === 'payments'    && <PaymentsTab />}
      {tab === 'api-keys'    && <ApiKeysTab />}
      {tab === 'webhooks'    && <WebhooksTab />}
      {tab === 'balance'     && <BalanceTab />}
      {tab === 'settlements' && <SettlementsTab />}
      {tab === 'invoices'    && <InvoicesTab />}
      {tab === 'reports'     && <ReportsTab />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 1 — DASHBOARD
═══════════════════════════════════════════════════════════════════ */
function DashboardTab() {
  const [profile, setProfile]   = useState(null);
  const [dash, setDash]         = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [settlementModal, setSettlementModal] = useState(false);
  const [settleAmount, setSettleAmount]       = useState('');
  const [settleNote, setSettleNote]           = useState('');
  const [settling, setSettling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d, pay] = await Promise.all([
        merchantAPI.getProfile(),
        merchantAPI.getDashboard(),
        merchantAPI.getPayments({ limit: 5, page: 1 }),
      ]);
      const _rawProfile = p.data?.merchant || p.merchant;
      const _wid = _rawProfile?.walletId;
      setProfile({ ..._rawProfile, walletId: _wid?._id?.toString() ?? _wid });
      setDash(d.data?.dashboard || d.dashboard || d.data || d);
      setPayments((pay.data?.payments || pay.payments) ?? []);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSettle = async () => {
    if (!settleAmount || isNaN(Number(settleAmount)) || Number(settleAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSettling(true);
    try {
      await merchantAPI.requestSettlement({ amount: Number(settleAmount), note: settleNote });
      toast.success('Settlement request submitted');
      setSettlementModal(false);
      setSettleAmount('');
      setSettleNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Settlement request failed');
    } finally {
      setSettling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  const merchant = profile ?? {};
  const today    = dash?.today    ?? {};
  const month    = dash?.thisMonth ?? {};
  const settle   = dash?.settlement ?? {};

  return (
    <div className="space-y-6">
      {/* Identity strip */}
      <Card className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-cyan/10 border border-cyan/20 flex items-center justify-center flex-shrink-0">
            <Store className="w-6 h-6 text-cyan" />
          </div>
          <div className="min-w-0">
            <p className="text-text-primary font-black text-base truncate">{merchant.businessName || '—'}</p>
            <p className="text-text-muted text-xs">{merchant.businessType || '—'}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 text-xs">
          <div>
            <p className="text-text-muted uppercase tracking-wider font-bold mb-0.5">Wallet ID</p>
            <p className="text-text-secondary font-mono">{truncate(merchant.walletId, 20)}</p>
          </div>
          <div>
            <p className="text-text-muted uppercase tracking-wider font-bold mb-0.5">Total Processed</p>
            <p className="text-cyan font-bold">{fmt(merchant.totalProcessed)}</p>
          </div>
          <div className="flex items-center">
            <Badge label={merchant.isActive ? 'Active' : 'Inactive'} color={merchant.isActive ? 'green' : 'red'} />
          </div>
        </div>
      </Card>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Today's Revenue"     value={fmt(today.revenue)}          sub={`${today.txCount ?? 0} transactions`} icon="💰" color="#00A8DB" />
        <StatCard label="Today's Transactions" value={today.txCount ?? 0}          sub={`Avg ${fmt(today.avgTx)}`}           icon="🔄" color="#10B981" />
        <StatCard label="Month Revenue"        value={fmt(month.revenue)}          sub={`${month.txCount ?? 0} transactions`} icon="📈" color="#8B5CF6" />
        <StatCard label="Pending Settlement"   value={fmt(settle.pending)}         sub={settle.lastSettled ? `Last: ${format(new Date(settle.lastSettled), 'MMM d')}` : 'No history'} icon="🏦" color="#F59E0B" />
      </div>

      {/* Recent payments */}
      <Card>
        <SectionHeader title="Recent Payments" />
        {payments.length === 0 ? (
          <Empty icon="💳" title="No payments yet" description="Payments will appear here once processed" />
        ) : (
          <Table headers={['Amount', 'Payer', 'Status', 'Time']}>
            {payments.map((p) => (
              <tr key={p._id} className="border-b border-border/30">
                <Td><span className="text-text-primary font-bold">{fmt(p.amount, p.currency)}</span></Td>
                <Td>{p.payer?.name || p.payer?.email || '—'}</Td>
                <Td><Badge label={p.status} color={statusColor(p.status)} /></Td>
                <Td>{format(new Date(p.createdAt), 'MMM d, HH:mm')}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/* Settlement section */}
      <Card>
        <SectionHeader
          title="Settlement"
          action={
            <Button size="sm" onClick={() => setSettlementModal(true)}>
              <Banknote className="w-4 h-4" />
              Request Settlement
            </Button>
          }
        />
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex-1 p-4 rounded-xl bg-bg-panel border border-border">
            <p className="text-text-muted text-xs uppercase tracking-wider font-bold mb-1">Pending Balance</p>
            <p className="text-amber font-black text-2xl">{fmt(settle.pending)}</p>
          </div>
          <div className="flex-1 p-4 rounded-xl bg-bg-panel border border-border">
            <p className="text-text-muted text-xs uppercase tracking-wider font-bold mb-1">Last Settled</p>
            <p className="text-text-primary font-bold text-lg">
              {settle.lastSettled ? format(new Date(settle.lastSettled), 'MMM d, yyyy') : '—'}
            </p>
          </div>
        </div>
      </Card>

      {/* Settlement modal */}
      <Modal open={settlementModal} onClose={() => setSettlementModal(false)} title="Request Settlement">
        <div className="space-y-4">
          <Input
            label="Amount (UGX)"
            type="number"
            placeholder="0"
            value={settleAmount}
            onChange={(e) => setSettleAmount(e.target.value)}
          />
          <Input
            label="Note (optional)"
            placeholder="Settlement note..."
            value={settleNote}
            onChange={(e) => setSettleNote(e.target.value)}
          />
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setSettlementModal(false)}>Cancel</Button>
            <Button className="flex-1" loading={settling} onClick={handleSettle}>Confirm Request</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 2 — PAYMENT LINKS
═══════════════════════════════════════════════════════════════════ */
function LinksTab() {
  const [links, setLinks]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [qrModal, setQrModal]       = useState(null); // link object
  const [newLink, setNewLink]       = useState(null); // freshly created link
  const [creating, setCreating]     = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [form, setForm] = useState({ title: '', amount: '', currency: 'UGX', description: '', maxUses: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await merchantAPI.getPaymentLinks({});
      setLinks(res.links ?? []);
    } catch {
      toast.error('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title || !form.amount) { toast.error('Title and amount are required'); return; }
    setCreating(true);
    try {
      const res = await merchantAPI.createPaymentLink({
        title: form.title,
        amount: Number(form.amount),
        currency: form.currency,
        description: form.description,
        maxUses: form.maxUses ? Number(form.maxUses) : undefined,
      });
      const created = res.data || res;
      toast.success('Payment link created');
      setForm({ title: '', amount: '', currency: 'UGX', description: '', maxUses: '' });
      setCreateModal(false);
      setNewLink(created);
      setQrModal(created);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (id) => {
    setDeactivating(true);
    try {
      await merchantAPI.deactivateLink(id);
      toast.success('Link deactivated');
      setQrModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to deactivate link');
    } finally {
      setDeactivating(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Copied!');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-text-primary font-bold">Payment Links</h2>
        <Button size="sm" onClick={() => setCreateModal(true)}>
          <Plus className="w-4 h-4" />
          Create Link
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Spinner size="lg" /></div>
      ) : links.length === 0 ? (
        <Card>
          <Empty
            icon="🔗"
            title="No payment links"
            description="Create your first payment link to start accepting payments"
            action={<Button size="sm" onClick={() => setCreateModal(true)}>Create Link</Button>}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {links.map((link) => (
            <Card key={link._id} className="flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-text-primary font-bold text-sm truncate">{link.title}</p>
                  <p className="text-cyan font-black text-lg">{fmt(link.amount, link.currency)}</p>
                </div>
                <Badge label={link.isActive ? 'Active' : 'Inactive'} color={link.isActive ? 'green' : 'gray'} />
              </div>
              <div className="flex items-center justify-between text-xs text-text-muted">
                <span>{link.usageCount ?? 0} uses</span>
                <span>{format(new Date(link.createdAt), 'MMM d, yyyy')}</span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setQrModal(link)}
              >
                <QrCode className="w-4 h-4" />
                View QR
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Create Link Modal */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Create Payment Link">
        <div className="space-y-4">
          <Input
            label="Title"
            placeholder="e.g. Product Payment"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Amount"
              type="number"
              placeholder="0"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            />
            <Select
              label="Currency"
              value={form.currency}
              onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
            >
              <option value="UGX">UGX</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="USDT">USDT</option>
            </Select>
          </div>
          <Input
            label="Description (optional)"
            placeholder="Describe what this link is for..."
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <Input
            label="Max Uses (optional)"
            type="number"
            placeholder="Unlimited"
            value={form.maxUses}
            onChange={(e) => setForm((f) => ({ ...f, maxUses: e.target.value }))}
          />
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setCreateModal(false)}>Cancel</Button>
            <Button className="flex-1" loading={creating} onClick={handleCreate}>Create Link</Button>
          </div>
        </div>
      </Modal>

      {/* QR Modal */}
      <Modal open={!!qrModal} onClose={() => setQrModal(null)} title="Payment Link QR Code" size="sm">
        {qrModal && (
          <div className="flex flex-col items-center gap-5">
            <div className="bg-white p-4 rounded-2xl">
              <QRCodeSVG value={qrModal.qrCode || qrModal.url || qrModal._id} size={200} />
            </div>
            <div className="w-full space-y-3">
              <div className="flex items-center gap-2 bg-bg-panel border border-border rounded-xl px-3 py-2">
                <p className="text-text-muted text-xs flex-1 truncate font-mono">{qrModal.url || '—'}</p>
                <button
                  onClick={() => copyUrl(qrModal.url)}
                  className="text-cyan hover:text-cyan/80 flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-text-muted">
                <div>
                  <p className="uppercase tracking-wider font-bold mb-0.5">Amount</p>
                  <p className="text-text-primary font-bold">{fmt(qrModal.amount, qrModal.currency)}</p>
                </div>
                <div>
                  <p className="uppercase tracking-wider font-bold mb-0.5">Uses</p>
                  <p className="text-text-primary font-bold">{qrModal.usageCount ?? 0}</p>
                </div>
              </div>
              {qrModal.isActive && (
                <Button
                  variant="danger"
                  size="sm"
                  className="w-full"
                  loading={deactivating}
                  onClick={() => handleDeactivate(qrModal._id)}
                >
                  <Power className="w-4 h-4" />
                  Deactivate Link
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 3 — PAYMENTS
═══════════════════════════════════════════════════════════════════ */
function PaymentsTab() {
  const [payments, setPayments]   = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [status, setStatus]       = useState('all');
  const [loading, setLoading]     = useState(true);

  const load = useCallback(async (page = 1, st = status) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (st !== 'all') params.status = st;
      const res = await merchantAPI.getPayments(params);
      setPayments(res.payments ?? []);
      const pg = res.pagination || {};
      setPagination({ page: pg.page || page, pages: pg.pages || 1, total: pg.total || 0 });
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(1, status); }, [status]);

  const StatusIcon = ({ s }) => {
    if (s === 'completed' || s === 'paid') return <CheckCircle className="w-3.5 h-3.5 text-green" />;
    if (s === 'pending' || s === 'processing') return <Clock className="w-3.5 h-3.5 text-amber" />;
    return <XCircle className="w-3.5 h-3.5 text-red" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <h2 className="text-text-primary font-bold">Payment History</h2>
        <div className="flex gap-2 flex-wrap">
          {['all', 'completed', 'pending', 'failed'].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                status === s
                  ? 'bg-cyan text-bg'
                  : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Spinner size="lg" /></div>
        ) : payments.length === 0 ? (
          <Empty icon="💳" title="No payments found" description="No payments match the selected filter" />
        ) : (
          <>
            <Table headers={['Amount', 'Currency', 'Payer', 'Link', 'Status', 'Date']}>
              {payments.map((p) => (
                <tr key={p._id} className="border-b border-border/30">
                  <Td><span className="text-text-primary font-bold">{fmt(p.amount, p.currency)}</span></Td>
                  <Td><span className="font-mono text-text-muted">{p.currency}</span></Td>
                  <Td>
                    <div>
                      <p className="text-text-primary text-xs font-semibold">{p.payer?.name || '—'}</p>
                      <p className="text-text-muted text-xs">{p.payer?.email || ''}</p>
                    </div>
                  </Td>
                  <Td><span className="text-text-muted text-xs">{truncate(p.paymentLink?.title, 20)}</span></Td>
                  <Td>
                    <div className="flex items-center gap-1.5">
                      <StatusIcon s={p.status} />
                      <Badge label={p.status} color={statusColor(p.status)} />
                    </div>
                  </Td>
                  <Td>{format(new Date(p.createdAt), 'MMM d, yyyy HH:mm')}</Td>
                </tr>
              ))}
            </Table>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <p className="text-text-muted text-xs">{pagination.total} total payments</p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={pagination.page <= 1}
                    onClick={() => load(pagination.page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-text-secondary text-xs font-semibold">
                    {pagination.page} / {pagination.pages}
                  </span>
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => load(pagination.page + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 4 — SETTLEMENTS
═══════════════════════════════════════════════════════════════════ */
function SettlementsTab() {
  const [settlements, setSettlements] = useState([]);
  const [pagination, setPagination]   = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading]         = useState(true);
  const [dash, setDash]               = useState(null);
  const [settleModal, setSettleModal] = useState(false);
  const [settleAmount, setSettleAmount] = useState('');
  const [settleNote, setSettleNote]   = useState('');
  const [settling, setSettling]       = useState(false);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const [sRes, dRes] = await Promise.all([
        merchantAPI.getSettlements({ page, limit: 15 }),
        merchantAPI.getDashboard(),
      ]);
      setSettlements((sRes.data?.docs || sRes.docs || sRes.data?.settlements || sRes.settlements) ?? []);
      const pg = sRes.data?.pagination || sRes.pagination || {};
      setPagination({ page: pg.page || page, pages: pg.pages || 1, total: pg.total || 0 });
      setDash(dRes.data || dRes);
    } catch {
      toast.error('Failed to load settlements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [feePreview, setFeePreview] = useState(null);
  const [settleCfg, setSettleCfg]   = useState(null);

  // Load settlement config
  useEffect(() => {
    merchantAPI.getSettlementConfig().then(r => setSettleCfg(r.config)).catch(() => {});
  }, []);

  // Preview fees when modal opens
  const openSettleModal = async () => {
    setSettleModal(true);
    try {
      const w = await merchantAPI.getDashboard();
      const bal = w.settlement?.pending || 0;
      if (bal > 0) {
        const fp = await merchantAPI.settlementFeePreview(bal);
        setFeePreview(fp.data || fp);
      }
    } catch {}
  };

  const handleSettle = async () => {
    setSettling(true);
    try {
      const r = await merchantAPI.requestSettlement({});
      toast.success('Settlement submitted — UGX ' + (r.netAmount || '').toLocaleString() + ' being sent');
      setSettleModal(false);
      setFeePreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Settlement request failed');
    } finally {
      setSettling(false);
    }
  };

  const settle = dash?.settlement ?? {};
  const month  = dash?.thisMonth  ?? {};

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <h2 className="text-text-primary font-bold">Settlements</h2>
        <Button size="sm" onClick={openSettleModal}>
          <Banknote className="w-4 h-4" />
          Request Settlement
        </Button>
      </div>

      {/* Stats box */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="text-center">
          <p className="text-text-muted text-xs uppercase tracking-wider font-bold mb-1">Settled This Month</p>
          <p className="text-green font-black text-xl">{fmt(month.revenue)}</p>
        </Card>
        <Card className="text-center">
          <p className="text-text-muted text-xs uppercase tracking-wider font-bold mb-1">Pending Balance</p>
          <p className="text-amber font-black text-xl">{fmt(settle.pending)}</p>
        </Card>
        <Card className="text-center">
          <p className="text-text-muted text-xs uppercase tracking-wider font-bold mb-1">Total Settlements</p>
          <p className="text-text-primary font-black text-xl">{pagination.total}</p>
        </Card>
      </div>

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Spinner size="lg" /></div>
        ) : settlements.length === 0 ? (
          <Empty icon="🏦" title="No settlements yet" description="Request a settlement to withdraw your earnings" />
        ) : (
          <>
            <Table headers={['Amount', 'Status', 'Requested', 'Completed']}>
              {settlements.map((s) => (
                <tr key={s._id} className="border-b border-border/30">
                  <Td><span className="text-text-primary font-bold">{fmt(s.amount, s.currency)}</span></Td>
                  <Td><Badge label={s.status} color={statusColor(s.status)} /></Td>
                  <Td>{s.createdAt ? format(new Date(s.createdAt), 'MMM d, yyyy') : '—'}</Td>
                  <Td>{s.completedAt ? format(new Date(s.completedAt), 'MMM d, yyyy') : <span className="text-text-muted">Pending</span>}</Td>
                </tr>
              ))}
            </Table>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <p className="text-text-muted text-xs">{pagination.total} total</p>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="xs" disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-text-secondary text-xs font-semibold">
                    {pagination.page} / {pagination.pages}
                  </span>
                  <Button variant="ghost" size="xs" disabled={pagination.page >= pagination.pages} onClick={() => load(pagination.page + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Settlement Modal */}
      <Modal open={settleModal} onClose={() => setSettleModal(false)} title="Request Settlement">
        <div className="space-y-4">
          {settleCfg && !settleCfg.payoutPhone && (
            <div className="p-3 bg-amber/10 border border-amber/30 rounded-lg text-amber text-sm">
              No payout phone configured. Update your settlement config first.
            </div>
          )}
          {feePreview && (
            <div className="p-3 bg-bg-panel border border-border rounded-lg space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-text-muted">Balance to settle</span><span className="text-text-primary font-semibold">UGX {(feePreview.amount || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-text-muted">Yo! B2C fee</span><span className="text-red-400">− UGX {(feePreview.yoFee || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-text-muted">DWI fee</span><span className="text-red-400">− UGX {(feePreview.platformFee || 0).toLocaleString()}</span></div>
              <div className="flex justify-between border-t border-border pt-2"><span className="text-text-primary font-bold">You receive</span><span className="text-green font-black">UGX {(feePreview.netAmount || 0).toLocaleString()}</span></div>
              <div className="text-text-muted text-xs">Sent to: {settleCfg?.payoutPhone || settleCfg?.phone || 'registered phone'}</div>
            </div>
          )}
          <p className="text-text-muted text-sm">Your full wallet balance will be settled. This cannot be undone.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setSettleModal(false)}>Cancel</Button>
            <Button className="flex-1" loading={settling} onClick={handleSettle} disabled={!!(settleCfg && !settleCfg.payoutPhone)}>Confirm Settlement</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 5 — REPORTS
═══════════════════════════════════════════════════════════════════ */
function ReportsTab() {
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await merchantAPI.getSalesReport({ days: 30 });
      setReport(res.data || res);
    } catch {
      toast.error('Failed to load sales report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadCSV = () => {
    const daily = report?.daily ?? [];
    if (daily.length === 0) { toast.error('No data to export'); return; }
    const header = 'Date,Revenue,Transactions\n';
    const rows = daily.map((d) => `${d.date},${d.revenue ?? 0},${d.txCount ?? 0}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `merchant-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24"><Spinner size="lg" /></div>
    );
  }

  const daily   = report?.daily   ?? [];
  const summary = report?.summary ?? {};

  // Build 7-day bars from last 7 items
  const chartData = daily.slice(-7).map((d) => ({
    name: d.date ? format(new Date(d.date), 'MMM d') : d.label || '—',
    revenue: d.revenue ?? 0,
  }));

  const bestDay = daily.reduce((best, d) => (d.revenue > (best?.revenue ?? 0) ? d : best), null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-text-primary font-bold">Sales Report</h2>
        <Button size="sm" variant="secondary" onClick={downloadCSV}>
          <Download className="w-4 h-4" />
          Download CSV
        </Button>
      </div>

      {/* Bar chart */}
      <Card>
        <SectionHeader title="Daily Revenue (Last 7 Days)" />
        {chartData.length === 0 ? (
          <Empty icon="📊" title="No chart data" description="Revenue data will appear once payments are processed" />
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#8FA5BF', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#8FA5BF', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                  width={48}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(6,200,232,0.06)' }} />
                <Bar dataKey="revenue" fill="#00A8DB" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="text-center">
          <p className="text-text-muted text-xs uppercase tracking-wider font-bold mb-1">Total Revenue (30d)</p>
          <p className="text-cyan font-black text-xl">{fmt(summary.totalRevenue)}</p>
        </Card>
        <Card className="text-center">
          <p className="text-text-muted text-xs uppercase tracking-wider font-bold mb-1">Avg Transaction</p>
          <p className="text-text-primary font-black text-xl">{fmt(summary.avgTransaction)}</p>
        </Card>
        <Card className="text-center">
          <p className="text-text-muted text-xs uppercase tracking-wider font-bold mb-1">Best Day</p>
          <p className="text-green font-black text-xl">
            {bestDay?.date ? format(new Date(bestDay.date), 'MMM d') : '—'}
          </p>
          {bestDay && (
            <p className="text-text-muted text-xs mt-0.5">{fmt(bestDay.revenue)}</p>
          )}
        </Card>
      </div>

      {/* Daily breakdown table */}
      {daily.length > 0 && (
        <Card>
          <SectionHeader title="30-Day Breakdown" />
          <Table headers={['Date', 'Revenue', 'Transactions', 'Avg Transaction']}>
            {daily.slice().reverse().map((d, i) => (
              <tr key={i} className="border-b border-border/30">
                <Td>{d.date ? format(new Date(d.date), 'MMM d, yyyy') : d.label || '—'}</Td>
                <Td><span className="text-text-primary font-bold">{fmt(d.revenue)}</span></Td>
                <Td>{d.txCount ?? 0}</Td>
                <Td>{d.txCount ? fmt(d.revenue / d.txCount) : '—'}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   TAB — CHARGES (API Charges)
═══════════════════════════════════════════════════════════════════ */
function ChargesTab() {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await merchantChargesAPI.listCharges({ page: p, limit: 20 });
      setCharges(r.docs || []);
      setTotal(r.total || 0);
      setPage(p);
    } catch (err) { toast.error(err.message || 'Failed to load charges'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pages = Math.ceil(total / 20);

  const statusColor = s => ({ succeeded:'green', failed:'red', expired:'gray', pending:'amber' }[s] || 'gray');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-text-primary font-bold text-sm">API Charges</h3>
          <p className="text-text-muted text-xs">{total} total charges via Merchant API</p>
        </div>
        <a href="/merchant/docs" target="_blank"
          className="text-xs text-cyan hover:underline">API Docs →</a>
      </div>
      {loading ? <div className="flex justify-center py-8"><Spinner /></div> :
        charges.length === 0 ? <Empty message="No charges yet" /> : (
        <Card>
          <Table headers={['Charge ID','Amount','Status','Rail','Fee','Date']}>
            {charges.map(c => (
              <tr key={c._id} className="border-b border-border/30">
                <Td><code className="text-xs text-cyan">{c.chargeId?.slice(0,16)}…</code></Td>
                <Td><span className="font-bold text-text-primary">{fmt(c.amount)}</span></Td>
                <Td><Badge color={statusColor(c.status)}>{c.status}</Badge></Td>
                <Td>{c.paymentRail || '—'}</Td>
                <Td>{c.feeAmount ? fmt(c.feeAmount) : '—'}</Td>
                <Td>{c.createdAt ? format(new Date(c.createdAt),'MMM d, HH:mm') : '—'}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
      {pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page<=1} onClick={() => load(page-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-text-muted py-2">{page}/{pages}</span>
          <Button size="sm" variant="outline" disabled={page>=pages} onClick={() => load(page+1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB — API KEYS
═══════════════════════════════════════════════════════════════════ */
function ApiKeysTab() {
  const [keys, setKeys]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);
  const [newMode, setNewMode]   = useState('live');
  const [newLabel, setNewLabel] = useState('');
  const [revealed, setRevealed] = useState(null); // { secretKey, label, mode }

  const load = async () => {
    setLoading(true);
    try {
      const r = await merchantChargesAPI.listApiKeys();
      setKeys(r.keys || []);
    } catch (err) { toast.error(err.message || 'Failed to load API keys'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const r = await merchantChargesAPI.createApiKey({ mode: newMode, label: newLabel });
      const k = r.key;
      setRevealed(k);
      setNewLabel('');
      load();
    } catch (err) { toast.error(err.message || 'Failed to create key'); }
    finally { setCreating(false); }
  };

  const revoke = async (keyId) => {
    if (!window.confirm('Revoke this API key? Any integrations using it will stop working.')) return;
    try {
      await merchantChargesAPI.revokeApiKey(keyId);
      toast.success('Key revoked');
      load();
    } catch (err) { toast.error(err.message || 'Failed to revoke key'); }
  };

  return (
    <div className="space-y-5">
      {revealed && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-300 font-semibold text-sm">New API Key Created</p>
              <p className="text-slate-400 text-xs mt-0.5">Copy the secret key — it will never be shown again.</p>
            </div>
            <button onClick={() => setRevealed(null)} className="text-slate-500 hover:text-white text-lg leading-none">×</button>
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-slate-400 mb-1">Public Key</p>
              <code className="text-xs font-mono text-slate-300 bg-slate-800 rounded px-2 py-1 block break-all">{revealed.publicKey}</code>
            </div>
            <div>
              <p className="text-xs text-amber-300 font-semibold mb-1">Secret Key (copy now)</p>
              <div className="flex gap-2">
                <code className="text-xs font-mono text-amber-200 bg-slate-900 border border-amber-500/30 rounded px-2 py-1 block break-all flex-1">{revealed.secretKey}</code>
                <button onClick={() => { navigator.clipboard.writeText(revealed.secretKey); toast.success('Copied'); }}
                  className="text-slate-400 hover:text-white px-2">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Card>
        <SectionHeader title="Create New Key" />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Select value={newMode} onChange={e => setNewMode(e.target.value)}>
            <option value="live">Live</option>
            <option value="test">Test</option>
          </Select>
          <Input placeholder="Label (optional)" value={newLabel} onChange={e => setNewLabel(e.target.value)} />
        </div>
        <Button className="mt-3" onClick={create} disabled={creating}>
          {creating ? 'Creating…' : 'Create API Key'}
        </Button>
      </Card>

      <Card>
        <SectionHeader title="Active Keys" />
        {loading ? <Spinner /> : keys.length === 0 ? <Empty message="No API keys" /> : (
          <div className="mt-3 space-y-2">
            {keys.map(k => (
              <div key={k._id} className="flex items-center justify-between p-3 bg-bg-surface rounded-xl border border-border">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge color={k.mode === 'live' ? 'green' : 'amber'}>{k.mode}</Badge>
                    <span className="text-text-primary text-sm font-medium">{k.label || 'Unnamed'}</span>
                    {!k.isActive && <Badge color="red">revoked</Badge>}
                  </div>
                  <p className="text-text-muted text-xs font-mono mt-0.5 truncate">{k.publicKey}</p>
                  <p className="text-text-muted text-xs mt-0.5">
                    Created {k.createdAt ? format(new Date(k.createdAt),'MMM d, yyyy') : '—'}
                    {k.lastUsedAt ? ` · Last used ${format(new Date(k.lastUsedAt),'MMM d')}` : ' · Never used'}
                  </p>
                </div>
                {k.isActive && (
                  <button onClick={() => revoke(k._id)}
                    className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/10 transition-all shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="text-xs text-text-muted bg-bg-panel border border-border rounded-xl p-4 space-y-1">
        <p className="font-semibold text-text-secondary">Quick start</p>
        <p>1. Create a <code className="text-cyan bg-bg-surface px-1 rounded">live</code> key above</p>
        <p>2. Call <code className="text-cyan bg-bg-surface px-1 rounded">POST /api/v1/merchant/charges</code> with <code className="text-cyan bg-bg-surface px-1 rounded">Bearer sk_live_...</code></p>
        <p>3. Share the <code className="text-cyan bg-bg-surface px-1 rounded">payUrl</code> with your customer</p>
        <p>4. Receive a <code className="text-cyan bg-bg-surface px-1 rounded">charge.succeeded</code> webhook when paid</p>
        <a href="/merchant/docs" target="_blank" className="text-cyan hover:underline block mt-2">Full API Documentation →</a>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB — WEBHOOKS
═══════════════════════════════════════════════════════════════════ */
function WebhooksTab() {
  const [config, setConfig]   = useState(null);
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [url, setUrl]         = useState('');
  const [showSecret, setShowSecret] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cr, er] = await Promise.all([
        merchantChargesAPI.getWebhookConfig(),
        merchantChargesAPI.getWebhookEvents({ limit: 20 }),
      ]);
      const cfg = cr.webhook;
      setConfig(cfg);
      setUrl(cfg?.url || '');
      setEvents(er.docs || []);
    } catch (err) { toast.error(err.message || 'Failed to load webhooks'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!url.startsWith('https://')) { toast.error('URL must start with https://'); return; }
    setSaving(true);
    try {
      await merchantChargesAPI.updateWebhookConfig({ url });
      toast.success('Webhook URL saved');
      load();
    } catch (err) { toast.error(err.message || 'Failed to save webhook'); }
    finally { setSaving(false); }
  };

  const deliveryColor = s => ({ delivered:'green', failed:'red', exhausted:'red', pending:'amber' }[s] || 'gray');

  return (
    <div className="space-y-5">
      <Card>
        <SectionHeader title="Webhook Endpoint" />
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-xs text-text-muted font-medium block mb-1">Endpoint URL (HTTPS)</label>
            <Input placeholder="https://yoursite.com/webhooks/dwi" value={url}
              onChange={e => setUrl(e.target.value)} />
          </div>
          {config?.signingSecret && (
            <div>
              <label className="text-xs text-text-muted font-medium block mb-1">Signing Secret</label>
              <div className="flex gap-2 items-center">
                <code className="text-xs font-mono text-text-secondary bg-bg-surface border border-border rounded-lg px-3 py-2 flex-1 truncate">
                  {showSecret ? config.signingSecret : 'whsec_••••••••••••••••••••'}
                </code>
                <button onClick={() => setShowSecret(s => !s)}
                  className="text-text-muted hover:text-text-primary p-2">
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {showSecret && (
                  <button onClick={() => { navigator.clipboard.writeText(config.signingSecret); toast.success('Copied'); }}
                    className="text-text-muted hover:text-text-primary p-2">
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-text-muted mt-1">Use this to verify the <code className="text-cyan">X-DWI-Signature</code> header</p>
            </div>
          )}
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Endpoint'}</Button>
        </div>
      </Card>

      <Card>
        <SectionHeader title="Recent Deliveries" />
        {loading ? <Spinner /> : events.length === 0 ? <Empty message="No deliveries yet" /> : (
          <Table headers={['Event','Charge','Status','Attempts','Response','When']}>
            {events.map(e => (
              <tr key={e._id} className="border-b border-border/30">
                <Td><code className="text-xs text-cyan">{e.event}</code></Td>
                <Td><code className="text-xs text-text-muted">{e.chargeId?.slice(0,12)}…</code></Td>
                <Td><Badge color={deliveryColor(e.status)}>{e.status}</Badge></Td>
                <Td>{e.attempts}/{e.maxAttempts}</Td>
                <Td>{e.lastResponseCode || '—'}</Td>
                <Td>{e.updatedAt ? format(new Date(e.updatedAt),'MMM d, HH:mm') : '—'}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB — BALANCE
═══════════════════════════════════════════════════════════════════ */
function BalanceTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [dashR, profileR] = await Promise.all([
        merchantAPI.getDashboard(),
        merchantAPI.getProfile(),
      ]);
      const dash    = dashR.data?.dashboard || dashR.dashboard || {};
      const profile = profileR.data?.merchant || profileR.merchant || {};
      setData({ dash, profile });
    } catch { toast.error('Failed to load balance'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;
  if (!data)   return <Empty message="Unable to load balance" />;

  const { dash, profile } = data;
  const gross    = profile.totalRevenue || 0;
  const fees     = profile.totalFees    || 0;
  const net      = gross - fees;
  const walletBal= dash.walletBalance   || 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Gross Collected" value={fmt(gross)} />
        <StatCard label="Total Fees Paid" value={fmt(fees)} />
        <StatCard label="Net Revenue" value={fmt(net)} />
        <StatCard label="Wallet Balance" value={fmt(walletBal)} />
      </div>
      <div className="bg-bg-panel border border-border rounded-xl p-4 text-sm">
        <p className="text-text-secondary font-semibold mb-2">About your balance</p>
        <p className="text-text-muted text-xs leading-relaxed">
          Net amounts from completed charges accumulate here. Settlement/payout to mobile money
          is available from the <strong className="text-text-secondary">Settlements</strong> tab.
          Fees shown are DWI platform fees (merchant_fee_wallet 1.5% or merchant_fee_momo 2.5%).
        </p>
      </div>
      <div className="text-xs text-text-muted">
        <p>Total payments received: <strong className="text-text-primary">{profile.totalPaymentsReceived || 0}</strong></p>
        <p>Settlement frequency: <strong className="text-text-primary">{profile.settlementFrequency || 'on_demand'}</strong></p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB — INVOICES
═══════════════════════════════════════════════════════════════════ */
function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ customerName:'', customerPhone:'', customerEmail:'', lineItems:[{ description:'', qty:1, unitPrice:'' }], dueDate:'', notes:'' });

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const r = await invoiceAPI.list({ page, limit: 15 });
      setInvoices(r.docs || []);
      setPagination({ page: r.page || page, pages: r.pages || 1, total: r.total || 0 });
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addItem = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, { description:'', qty:1, unitPrice:'' }] }));
  const removeItem = (i) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_,idx) => idx!==i) }));
  const updateItem = (i, k, v) => setForm(f => {
    const items = [...f.lineItems];
    items[i] = { ...items[i], [k]: v };
    return { ...f, lineItems: items };
  });

  const total = form.lineItems.reduce((s, li) => s + (Number(li.qty)||0)*(Number(li.unitPrice)||0), 0);

  const handleCreate = async () => {
    const lineItems = form.lineItems.map(li => ({ description: li.description, qty: Number(li.qty), unitPrice: Number(li.unitPrice) }));
    if (lineItems.some(li => !li.description || li.qty<=0 || li.unitPrice<0)) { toast.error('Fill all line items'); return; }
    setCreating(true);
    try {
      const created = await invoiceAPI.create({ ...form, lineItems });
      const payUrl = window.location.origin + '/pay/i/' + created.invoice.invoiceId;
      navigator.clipboard.writeText(payUrl).catch(() => {});
      toast.success('Invoice created — pay link copied to clipboard');
      setShowCreate(false);
      setForm({ customerName:'', customerPhone:'', customerEmail:'', lineItems:[{ description:'', qty:1, unitPrice:'' }], dueDate:'', notes:'' });
      load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed to create invoice'); }
    finally { setCreating(false); }
  };

  const handleSend = async (inv) => {
    try {
      await invoiceAPI.send(inv.invoiceId);
      toast.success('Invoice sent');
      load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed to send'); }
  };

  const handleCancel = async (inv) => {
    if (!window.confirm('Cancel this invoice?')) return;
    try {
      await invoiceAPI.cancel(inv.invoiceId);
      toast.success('Invoice cancelled');
      load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed to cancel'); }
  };

  const copyLink = (inv) => {
    const url = window.location.origin + '/pay/i/' + inv.invoiceId;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied'));
  };

  const statusColor = (s) => ({ draft:'text-text-muted', sent:'amber', paid:'green', overdue:'red', cancelled:'text-text-muted' }[s] || 'text-text-muted');

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <h2 className="text-text-primary font-bold">Invoices</h2>
        <Button size="sm" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> New Invoice</Button>
      </div>

      <Card>
        {loading ? <div className="flex items-center justify-center py-20"><Spinner size="lg" /></div>
        : invoices.length === 0 ? <Empty icon="🧾" title="No invoices yet" description="Create an invoice to send to your customers" />
        : (
          <>
            <Table headers={['Invoice #', 'Customer', 'Amount', 'Status', 'Actions']}>
              {invoices.map(inv => (
                <tr key={inv._id} className="border-b border-border/30">
                  <Td><span className="text-text-primary font-mono text-xs">{inv.invoiceNumber || inv.invoiceId.slice(0,12)}</span></Td>
                  <Td><span className="text-text-secondary">{inv.customerName || '—'}</span></Td>
                  <Td><span className="text-text-primary font-bold">UGX {(inv.totalAmount||0).toLocaleString()}</span></Td>
                  <Td><Badge label={inv.status} color={statusColor(inv.status)} /></Td>
                  <Td>
                    <div className="flex gap-2">
                      {inv.status === 'draft' && <Button size="xs" variant="ghost" onClick={() => handleSend(inv)}><Send className="w-3 h-3" /></Button>}
                      {(inv.status === 'draft' || inv.status === 'sent' || inv.status === 'overdue') && <Button size="xs" variant="ghost" onClick={() => copyLink(inv)} title="Copy pay link"><Copy className="w-3 h-3" /></Button>}
                      {(inv.status === 'draft' || inv.status === 'sent') && <Button size="xs" variant="ghost" onClick={() => handleCancel(inv)}><XCircle className="w-3 h-3" /></Button>}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <p className="text-text-muted text-xs">{pagination.total} total</p>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="xs" disabled={pagination.page<=1} onClick={() => load(pagination.page-1)}><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-text-secondary text-xs font-semibold">{pagination.page}/{pagination.pages}</span>
                  <Button variant="ghost" size="xs" disabled={pagination.page>=pagination.pages} onClick={() => load(pagination.page+1)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create Invoice Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Invoice" wide>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Customer Name" value={form.customerName} onChange={e => setForm(f=>({...f,customerName:e.target.value}))} placeholder="John Doe" />
            <Input label="Customer Phone" value={form.customerPhone} onChange={e => setForm(f=>({...f,customerPhone:e.target.value}))} placeholder="07XXXXXXXX" />
            <Input label="Customer Email" value={form.customerEmail} onChange={e => setForm(f=>({...f,customerEmail:e.target.value}))} placeholder="email@example.com" />
            <Input label="Due Date" type="date" value={form.dueDate} onChange={e => setForm(f=>({...f,dueDate:e.target.value}))} />
          </div>

          <div>
            <p className="text-text-secondary text-sm font-semibold mb-2">Line Items</p>
            {form.lineItems.map((li, i) => (
              <div key={i} className="flex gap-2 mb-2 items-start">
                <input value={li.description} onChange={e=>updateItem(i,'description',e.target.value)} placeholder="Description" className="flex-1 px-3 py-2 bg-bg-panel border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-cyan" />
                <input type="number" min="0.01" value={li.qty} onChange={e=>updateItem(i,'qty',e.target.value)} placeholder="Qty" className="w-16 px-3 py-2 bg-bg-panel border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-cyan" />
                <input type="number" min="0" value={li.unitPrice} onChange={e=>updateItem(i,'unitPrice',e.target.value)} placeholder="Price" className="w-24 px-3 py-2 bg-bg-panel border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-cyan" />
                {form.lineItems.length > 1 && <Button size="xs" variant="ghost" onClick={() => removeItem(i)}><Trash2 className="w-3 h-3" /></Button>}
              </div>
            ))}
            <Button size="xs" variant="ghost" onClick={addItem}><Plus className="w-3 h-3" /> Add line</Button>
          </div>

          {total > 0 && <div className="flex justify-between items-center p-3 bg-bg-panel rounded-lg"><span className="text-text-muted text-sm">Total</span><span className="text-text-primary font-black">UGX {Math.round(total).toLocaleString()}</span></div>}

          <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Notes (optional)..." rows={2} className="w-full px-3 py-2 bg-bg-panel border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-cyan resize-none" />

          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="flex-1" loading={creating} onClick={handleCreate}>Create Invoice</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
